import { Howl, Howler } from 'howler';
import type { WizardType } from '../wizards/WizardTypes';

type SoundName =
  | 'fireball_cast'
  | 'fireball_impact'
  | 'iceShard_cast'
  | 'iceShard_impact'
  | 'lightningBolt_cast'
  | 'lightningBolt_impact'
  | 'heal_cast'
  | 'healSpell_cast'
  | 'shield_cast'
  | 'shield_hit'
  | 'shield_break'
  | 'menu_click'
  | 'menu_hover'
  | 'victory'
  | 'defeat'
  | 'cooldown_ready'
  | 'damage_taken'
  | 'cheer_applause'
  | 'cheer_wow'
  | 'cheer_laugh'
  | 'cheer_boo'
  | 'heartbeat'
  | 'gesture_confirm'
  | 'combo_hit'
  | 'achievement_unlock';

type CheerType = 'applause' | 'wow' | 'laugh' | 'boo';

// Wizard sound profile: frequency multiplier and timbre characteristics
interface WizardSoundProfile {
  freqMult: number;       // Base frequency multiplier
  noiseMix: number;       // How much noise/texture to add
  harmonicDecay: number;  // How fast upper harmonics fall off
  reverbMix: number;      // Reverb/echo amount
}

const WIZARD_SOUND_PROFILES: Record<WizardType, WizardSoundProfile> = {
  pyromancer: { freqMult: 0.7, noiseMix: 0.4, harmonicDecay: 0.6, reverbMix: 0.3 },
  frostmage: { freqMult: 1.5, noiseMix: 0.15, harmonicDecay: 0.9, reverbMix: 0.5 },
  battlemage: { freqMult: 0.8, noiseMix: 0.35, harmonicDecay: 0.5, reverbMix: 0.2 },
  archmage: { freqMult: 1.1, noiseMix: 0.2, harmonicDecay: 0.85, reverbMix: 0.6 },
  necromancer: { freqMult: 0.75, noiseMix: 0.3, harmonicDecay: 0.7, reverbMix: 0.7 },
  druid: { freqMult: 1.0, noiseMix: 0.25, harmonicDecay: 0.8, reverbMix: 0.4 },
  chronomancer: { freqMult: 1.3, noiseMix: 0.15, harmonicDecay: 0.9, reverbMix: 0.55 },
};

export class AudioManager {
  private sounds: Map<SoundName, Howl> = new Map();
  private music: Howl | null = null;
  private battleMusic: Howl | null = null;
  private battleMusicIntense: Howl | null = null;
  private victoryMusic: Howl | null = null;
  private defeatMusic: Howl | null = null;
  private arenaAmbience: Howl | null = null;
  private musicVolume: number = 0.3;
  private sfxVolume: number = 0.7;
  private isMuted: boolean = false;
  private isInitialized: boolean = false;
  private audioContext: AudioContext | null = null;
  private blobUrls: string[] = []; // Track blob URLs for cleanup

  // Dynamic intensity state
  private currentIntensity: number = 0; // 0 = calm, 1 = maximum tension
  private targetIntensity: number = 0;
  private intensityRampTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatActive: boolean = false;

  // Combo tracking for escalating sounds
  private comboCount: number = 0;
  private lastComboTime: number = 0;
  private static readonly COMBO_TIMEOUT = 5000; // 5 seconds between hits to maintain combo

  // Wizard-specific sound caches
  private wizardSounds: Map<string, Howl> = new Map();

  async init(): Promise<void> {
    this.createSynthesizedSounds();
    this.isInitialized = true;
    console.log('Audio manager initialized with dynamic synthesized sounds');
  }

  private createSynthesizedSounds(): void {
    this.audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const audioContext = this.audioContext;

    this.sounds.set('fireball_cast', this.createSynthSound(audioContext, 'fireball_cast'));
    this.sounds.set('fireball_impact', this.createSynthSound(audioContext, 'fireball_impact'));
    this.sounds.set('iceShard_cast', this.createSynthSound(audioContext, 'iceShard_cast'));
    this.sounds.set('iceShard_impact', this.createSynthSound(audioContext, 'iceShard_impact'));
    this.sounds.set('shield_cast', this.createSynthSound(audioContext, 'shield_cast'));
    this.sounds.set('shield_hit', this.createSynthSound(audioContext, 'shield_hit'));
    this.sounds.set('shield_break', this.createSynthSound(audioContext, 'shield_break'));
    this.sounds.set('menu_click', this.createSynthSound(audioContext, 'menu_click'));
    this.sounds.set('menu_hover', this.createSynthSound(audioContext, 'menu_hover'));
    this.sounds.set('victory', this.createSynthSound(audioContext, 'victory'));
    this.sounds.set('defeat', this.createSynthSound(audioContext, 'defeat'));
    this.sounds.set('cooldown_ready', this.createSynthSound(audioContext, 'cooldown_ready'));
    this.sounds.set('damage_taken', this.createSynthSound(audioContext, 'damage_taken'));

    // Cheer sounds
    this.sounds.set('cheer_applause', this.createSynthSound(audioContext, 'cheer_applause'));
    this.sounds.set('cheer_wow', this.createSynthSound(audioContext, 'cheer_wow'));
    this.sounds.set('cheer_laugh', this.createSynthSound(audioContext, 'cheer_laugh'));
    this.sounds.set('cheer_boo', this.createSynthSound(audioContext, 'cheer_boo'));

    // Lightning bolt and heal spell sounds
    this.sounds.set('lightningBolt_cast', this.createSynthSound(audioContext, 'lightningBolt_cast'));
    this.sounds.set('lightningBolt_impact', this.createSynthSound(audioContext, 'lightningBolt_impact'));
    this.sounds.set('heal_cast', this.createSynthSound(audioContext, 'heal_cast'));
    this.sounds.set('healSpell_cast', this.createSynthSound(audioContext, 'heal_cast'));

    // New dynamic sounds
    this.sounds.set('heartbeat', this.createSynthSound(audioContext, 'heartbeat'));
    this.sounds.set('gesture_confirm', this.createSynthSound(audioContext, 'gesture_confirm'));
    this.sounds.set('combo_hit', this.createSynthSound(audioContext, 'combo_hit'));
    this.sounds.set('achievement_unlock', this.createSynthSound(audioContext, 'achievement_unlock'));

    this.createAmbientMusic(audioContext);
    this.createBattleMusic(audioContext);
    this.createBattleMusicIntense(audioContext);
    this.createVictoryMusic(audioContext);
    this.createDefeatMusic(audioContext);
    this.createArenaAmbience(audioContext);

    // Pre-generate wizard-specific spell variants
    this.generateWizardSoundVariants(audioContext);
  }

  // Set wizard types for sound variation (no-op; types are passed per-call to playSpellSound)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setWizardTypes(_player: WizardType, _enemy: WizardType): void {
  }

  // Play a spell sound with wizard-specific timbre variation
  playSpellSound(name: SoundName, wizardType?: WizardType): void {
    if (this.isMuted || !this.isInitialized) return;

    // Try wizard-specific variant first
    if (wizardType) {
      const key = `${name}_${wizardType}`;
      const wizSound = this.wizardSounds.get(key);
      if (wizSound) {
        wizSound.volume(this.sfxVolume);
        wizSound.play();
        return;
      }
    }

    // Fall back to default sound
    this.play(name);
  }

  // Play with spatial stereo panning (-1 = left, 0 = center, 1 = right)
  playSpatial(name: SoundName, pan: number): void {
    if (this.isMuted || !this.isInitialized) return;

    const sound = this.sounds.get(name);
    if (sound) {
      sound.volume(this.sfxVolume);
      sound.stereo(Math.max(-1, Math.min(1, pan)));
      sound.play();
    }
  }

  // Play spell with spatial positioning from caster to target
  // casterX and targetX are world-space X positions (-8 to 8 typical range)
  playSpellSpatial(name: SoundName, casterX: number, targetX: number, wizardType?: WizardType): void {
    if (this.isMuted || !this.isInitialized) return;

    // Normalize X positions to stereo pan (-1 to 1)
    const pan = (casterX + targetX) / 2 / 8;

    // Try wizard-specific variant
    if (wizardType) {
      const key = `${name}_${wizardType}`;
      const wizSound = this.wizardSounds.get(key);
      if (wizSound) {
        wizSound.volume(this.sfxVolume);
        wizSound.stereo(Math.max(-1, Math.min(1, pan)));
        wizSound.play();
        return;
      }
    }

    this.playSpatial(name, pan);
  }

  // Update battle intensity based on health percentages (0-1)
  updateBattleIntensity(playerHealthPct: number, enemyHealthPct: number): void {
    const minHealth = Math.min(playerHealthPct, enemyHealthPct);
    const avgHealth = (playerHealthPct + enemyHealthPct) / 2;

    // Calculate target intensity: low health = high intensity
    let intensity = 0;
    if (minHealth < 0.5) {
      intensity = (0.5 - minHealth) * 1.5; // Ramps from 0 at 50% to 0.75 at 0%
    }
    if (avgHealth < 0.35) {
      intensity += 0.25; // Both players low = extra tension
    }
    this.targetIntensity = Math.min(1, intensity);

    // Start/stop heartbeat based on player health
    if (playerHealthPct < 0.3 && !this.heartbeatActive) {
      this.startHeartbeat(playerHealthPct);
    } else if (playerHealthPct >= 0.3 && this.heartbeatActive) {
      this.stopHeartbeat();
    } else if (this.heartbeatActive && playerHealthPct < 0.3) {
      // Update heartbeat rate as health drops
      this.updateHeartbeatRate(playerHealthPct);
    }

    // Smooth intensity ramping
    if (!this.intensityRampTimer) {
      this.intensityRampTimer = setInterval(() => this.rampIntensity(), 50);
    }
  }

  private rampIntensity(): void {
    const rampSpeed = 0.02; // Smooth transitions
    if (Math.abs(this.currentIntensity - this.targetIntensity) < rampSpeed) {
      this.currentIntensity = this.targetIntensity;
    } else if (this.currentIntensity < this.targetIntensity) {
      this.currentIntensity += rampSpeed;
    } else {
      this.currentIntensity -= rampSpeed;
    }

    // Crossfade between normal and intense battle music
    if (this.battleMusic && this.battleMusicIntense) {
      const normalVol = this.musicVolume * 0.8 * (1 - this.currentIntensity);
      const intenseVol = this.musicVolume * 0.9 * this.currentIntensity;
      this.battleMusic.volume(normalVol);
      this.battleMusicIntense.volume(intenseVol);
    }
  }

  private startHeartbeat(healthPct: number): void {
    this.heartbeatActive = true;
    const interval = this.getHeartbeatInterval(healthPct);
    this.heartbeatTimer = setInterval(() => {
      this.play('heartbeat');
    }, interval);
  }

  private stopHeartbeat(): void {
    this.heartbeatActive = false;
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private updateHeartbeatRate(healthPct: number): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    const interval = this.getHeartbeatInterval(healthPct);
    this.heartbeatTimer = setInterval(() => {
      this.play('heartbeat');
    }, interval);
  }

  private getHeartbeatInterval(healthPct: number): number {
    // Faster heartbeat as health drops: 800ms at 30% -> 400ms at 5%
    return Math.max(400, 800 + (healthPct - 0.3) * 1600);
  }

  // Register a combo hit and play escalating combo sound
  registerComboHit(): void {
    const now = Date.now();
    if (now - this.lastComboTime > AudioManager.COMBO_TIMEOUT) {
      this.comboCount = 0;
    }
    this.comboCount++;
    this.lastComboTime = now;

    if (this.comboCount > 1) {
      this.playComboSound(this.comboCount);
    }
  }

  private playComboSound(combo: number): void {
    if (this.isMuted || !this.isInitialized || !this.audioContext) return;

    // Generate a quick escalating tone based on combo count
    const ctx = this.audioContext;
    const duration = 0.2;
    const sampleRate = ctx.sampleRate;
    const numSamples = Math.floor(duration * sampleRate);
    const buffer = ctx.createBuffer(1, numSamples, sampleRate);
    const data = buffer.getChannelData(0);

    // Pitch rises with each combo (capped at 8)
    const basePitch = 600 + Math.min(combo, 8) * 150;

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const env = Math.exp(-t * 12) * Math.min(1, t * 80);
      data[i] = env * (
        Math.sin(2 * Math.PI * basePitch * t) * 0.3 +
        Math.sin(2 * Math.PI * basePitch * 1.5 * t) * 0.2 +
        Math.sin(2 * Math.PI * basePitch * 2 * t) * 0.1
      );
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);

    const comboHowl = new Howl({
      src: [url],
      volume: this.sfxVolume * 0.6,
      format: ['wav']
    });
    comboHowl.play();
  }

  // Play gesture recognition confirmation sound
  playGestureConfirm(): void {
    this.play('gesture_confirm');
  }

  // Play crowd cheer with spatial surround effect (from "stands")
  playCheerSpatial(cheerType: CheerType): void {
    if (this.isMuted || !this.isInitialized) return;

    const soundName = `cheer_${cheerType}` as SoundName;
    const sound = this.sounds.get(soundName);
    if (sound) {
      // Randomize stereo position for surround crowd effect
      const pan = (Math.random() - 0.5) * 1.6; // Wider spread
      sound.volume(this.sfxVolume * 0.8);
      sound.stereo(Math.max(-1, Math.min(1, pan)));
      sound.play();
    }
  }

  private generateWizardSoundVariants(ctx: AudioContext): void {
    const spellSounds: SoundName[] = [
      'fireball_cast', 'fireball_impact',
      'iceShard_cast', 'iceShard_impact',
      'lightningBolt_cast', 'lightningBolt_impact',
      'heal_cast',
      'shield_cast', 'shield_hit', 'shield_break'
    ];
    const wizardTypes: WizardType[] = ['pyromancer', 'frostmage', 'battlemage', 'archmage', 'necromancer', 'druid', 'chronomancer'];

    for (const wType of wizardTypes) {
      const profile = WIZARD_SOUND_PROFILES[wType];
      for (const sound of spellSounds) {
        const key = `${sound}_${wType}`;
        this.wizardSounds.set(key, this.createWizardVariantSound(ctx, sound, profile));
      }
    }
  }

  private createWizardVariantSound(ctx: AudioContext, type: SoundName, profile: WizardSoundProfile): Howl {
    const duration = this.getSoundDuration(type);
    const sampleRate = ctx.sampleRate;
    const numSamples = Math.floor(duration * sampleRate);
    const buffer = ctx.createBuffer(1, numSamples, sampleRate);
    const data = buffer.getChannelData(0);

    // Generate the base sound
    this.generateSoundData(data, type, sampleRate);

    // Apply wizard-specific transformations
    this.applyWizardProfile(data, sampleRate, profile);

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);

    return new Howl({
      src: [url],
      volume: this.sfxVolume,
      format: ['wav']
    });
  }

  private applyWizardProfile(data: Float32Array, sampleRate: number, profile: WizardSoundProfile): void {
    const numSamples = data.length;

    // Apply frequency shift via resampling approximation and add character
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;

      // Add noise texture based on wizard type
      const noiseContrib = (Math.random() - 0.5) * profile.noiseMix * Math.exp(-t * 4);

      // Add harmonic resonance (shimmer for frost, rumble for fire)
      let resonance = 0;
      if (profile.freqMult > 1) {
        // High-frequency shimmer (frostmage, archmage)
        resonance = Math.sin(2 * Math.PI * 3000 * profile.freqMult * t) * 0.08 *
          Math.exp(-t * 6) * profile.harmonicDecay;
      } else {
        // Low-frequency rumble (pyromancer, battlemage)
        resonance = Math.sin(2 * Math.PI * 60 / profile.freqMult * t) * 0.12 *
          Math.exp(-t * 3) * (1 - profile.harmonicDecay);
      }

      // Add reverb-like tail
      const reverbTail = data[i] * profile.reverbMix * Math.exp(-t * 2) * 0.3;

      data[i] = data[i] * (1 - profile.noiseMix * 0.3) + noiseContrib + resonance + reverbTail;

      // Clamp to prevent clipping
      data[i] = Math.max(-0.95, Math.min(0.95, data[i]));
    }
  }

  private createSynthSound(ctx: AudioContext, type: SoundName): Howl {
    const duration = this.getSoundDuration(type);
    const sampleRate = ctx.sampleRate;
    const numSamples = Math.floor(duration * sampleRate);
    const buffer = ctx.createBuffer(1, numSamples, sampleRate);
    const data = buffer.getChannelData(0);

    this.generateSoundData(data, type, sampleRate);

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url); // Track for cleanup

    return new Howl({
      src: [url],
      volume: this.sfxVolume,
      format: ['wav']
    });
  }

  private getSoundDuration(type: SoundName): number {
    switch (type) {
      case 'fireball_cast':
      case 'iceShard_cast':
      case 'lightningBolt_cast':
      case 'shield_cast':
        return 0.5;
      case 'heal_cast':
      case 'healSpell_cast':
        return 0.8;
      case 'fireball_impact':
      case 'iceShard_impact':
      case 'lightningBolt_impact':
      case 'shield_hit':
      case 'shield_break':
        return 0.4;
      case 'menu_click':
      case 'menu_hover':
      case 'cooldown_ready':
      case 'gesture_confirm':
        return 0.15;
      case 'victory':
      case 'defeat':
        return 1.5;
      case 'damage_taken':
        return 0.3;
      case 'heartbeat':
        return 0.6;
      case 'combo_hit':
        return 0.2;
      case 'achievement_unlock':
        return 1.2;
      case 'cheer_applause':
        return 0.8;
      case 'cheer_wow':
        return 0.6;
      case 'cheer_laugh':
        return 0.7;
      case 'cheer_boo':
        return 0.6;
      default:
        return 0.3;
    }
  }

  private generateSoundData(data: Float32Array, type: SoundName, sampleRate: number): void {
    const numSamples = data.length;

    switch (type) {
      case 'fireball_cast':
        // Fiery whoosh with crackling - low rumble rising to roar
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 2.5) * Math.min(1, t * 15);
          // Rising roar
          const freq = 100 + t * 300 + Math.sin(t * 50) * 30;
          // Crackling noise
          const crackle = (Math.random() - 0.5) * Math.exp(-((t - 0.1) * (t - 0.1)) * 100) * 0.8;
          // Harmonics for warmth
          data[i] = envelope * (
            Math.sin(2 * Math.PI * freq * t) * 0.4 +
            Math.sin(2 * Math.PI * freq * 2 * t) * 0.2 +
            Math.sin(2 * Math.PI * freq * 3 * t) * 0.1 +
            (Math.random() - 0.5) * 0.25 * Math.exp(-t * 5)
          ) + crackle;
        }
        break;

      case 'fireball_impact':
        // Deep explosion with rumble
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 6) * Math.min(1, t * 100);
          // Low boom
          const boom = Math.sin(2 * Math.PI * 60 * t) * 0.6;
          // Mid crunch
          const crunch = Math.sin(2 * Math.PI * 150 * t * Math.exp(-t * 4)) * 0.4;
          // Debris noise
          const debris = (Math.random() - 0.5) * 0.5 * Math.exp(-t * 8);
          data[i] = envelope * (boom + crunch) + debris * Math.exp(-t * 3);
        }
        break;

      case 'iceShard_cast':
        // Crystalline shimmer with high-pitched whistle
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 3) * Math.min(1, t * 25);
          // High crystalline tones
          const crystal1 = Math.sin(2 * Math.PI * 2000 * t) * 0.2;
          const crystal2 = Math.sin(2 * Math.PI * 2500 * t) * 0.15;
          const crystal3 = Math.sin(2 * Math.PI * 3200 * t) * 0.1;
          // Shimmering modulation
          const shimmer = Math.sin(2 * Math.PI * 15 * t) * 0.3 + 0.7;
          // Whistle sweep down
          const whistle = Math.sin(2 * Math.PI * (1500 - t * 800) * t) * 0.25;
          data[i] = envelope * shimmer * (crystal1 + crystal2 + crystal3 + whistle);
        }
        break;

      case 'iceShard_impact':
        // Glass shatter with tinkling
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 12);
          // Sharp initial crack
          const crack = Math.exp(-t * 50) * (Math.random() - 0.5) * 0.8;
          // Tinkling fragments
          const tinkle1 = Math.sin(2 * Math.PI * 3000 * t) * Math.exp(-t * 15) * 0.3;
          const tinkle2 = Math.sin(2 * Math.PI * 4000 * t) * Math.exp(-t * 12) * 0.2;
          const tinkle3 = Math.sin(2 * Math.PI * 2400 * t) * Math.exp(-t * 10) * 0.25;
          data[i] = crack + envelope * (tinkle1 + tinkle2 + tinkle3);
        }
        break;

      case 'shield_cast':
        // Mystical hum with ethereal resonance
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = (1 - Math.exp(-t * 8)) * Math.exp(-t * 1.5);
          // Deep mystical drone
          const drone = Math.sin(2 * Math.PI * 120 * t) * 0.3;
          // Harmonic series for richness
          const harm1 = Math.sin(2 * Math.PI * 240 * t) * 0.2;
          const harm2 = Math.sin(2 * Math.PI * 360 * t) * 0.15;
          const harm3 = Math.sin(2 * Math.PI * 480 * t) * 0.1;
          // Ethereal shimmer
          const shimmer = Math.sin(2 * Math.PI * 800 * t) * Math.sin(2 * Math.PI * 6 * t) * 0.15;
          // Rising magical tone
          const magic = Math.sin(2 * Math.PI * (200 + t * 400) * t) * 0.2 * Math.exp(-t * 3);
          data[i] = envelope * (drone + harm1 + harm2 + harm3 + shimmer + magic);
        }
        break;

      case 'shield_hit':
        // Metallic gong with energy ripple
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 4);
          // Gong-like fundamental
          const gong = Math.sin(2 * Math.PI * 180 * t) * 0.4;
          // Inharmonic overtones (bell-like)
          const over1 = Math.sin(2 * Math.PI * 450 * t) * 0.25 * Math.exp(-t * 6);
          const over2 = Math.sin(2 * Math.PI * 720 * t) * 0.15 * Math.exp(-t * 8);
          const over3 = Math.sin(2 * Math.PI * 1100 * t) * 0.1 * Math.exp(-t * 10);
          // Energy ripple
          const ripple = Math.sin(2 * Math.PI * 300 * t) * Math.sin(2 * Math.PI * 20 * t) * 0.2;
          data[i] = envelope * (gong + over1 + over2 + over3 + ripple);
        }
        break;

      case 'shield_break':
        // Shattering barrier with energy discharge
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 3);
          // Initial burst
          const burst = Math.exp(-t * 20) * (Math.random() - 0.5) * 0.6;
          // Descending energy whine
          const whine = Math.sin(2 * Math.PI * (600 - t * 400) * t) * 0.3 * Math.exp(-t * 4);
          // Fragments
          const frag = (Math.random() - 0.5) * 0.4 * Math.exp(-t * 6);
          // Low thud
          const thud = Math.sin(2 * Math.PI * 80 * t) * 0.4 * Math.exp(-t * 8);
          data[i] = burst + envelope * (whine + thud) + frag;
        }
        break;

      case 'menu_click':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 30);
          data[i] = envelope * Math.sin(2 * Math.PI * 800 * t) * 0.5;
        }
        break;

      case 'menu_hover':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 20);
          data[i] = envelope * Math.sin(2 * Math.PI * 600 * t) * 0.3;
        }
        break;

      case 'victory':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 5) * Math.exp(-t * 0.5);
          const notes = [523.25, 659.25, 783.99, 1046.5];
          let value = 0;
          notes.forEach((freq, idx) => {
            const noteStart = idx * 0.3;
            if (t > noteStart) {
              const noteT = t - noteStart;
              value += Math.exp(-noteT * 2) * Math.sin(2 * Math.PI * freq * noteT) * 0.25;
            }
          });
          data[i] = envelope * value;
        }
        break;

      case 'defeat':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 3) * Math.exp(-t * 0.8);
          const freq = 300 - t * 150;
          data[i] = envelope * (
            Math.sin(2 * Math.PI * freq * t) * 0.4 +
            Math.sin(2 * Math.PI * freq * 0.5 * t) * 0.3
          );
        }
        break;

      case 'cooldown_ready':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 15);
          data[i] = envelope * (
            Math.sin(2 * Math.PI * 880 * t) * 0.4 +
            Math.sin(2 * Math.PI * 1320 * t) * 0.3
          );
        }
        break;

      case 'damage_taken':
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 10);
          data[i] = envelope * (
            Math.sin(2 * Math.PI * 150 * t) * 0.5 +
            (Math.random() - 0.5) * 0.3
          );
        }
        break;

      case 'lightningBolt_cast':
        // Electrical crackling with rising zap
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 2) * Math.min(1, t * 20);
          // Electrical buzz with harmonics
          const buzz = Math.sin(2 * Math.PI * 120 * t) * 0.2;
          const zap = Math.sin(2 * Math.PI * (400 + t * 800) * t) * 0.3 * Math.exp(-t * 3);
          // Crackling static
          const static_ = (Math.random() > 0.7 ? (Math.random() - 0.5) : 0) * 0.4 * envelope;
          // High-frequency sizzle
          const sizzle = Math.sin(2 * Math.PI * 3500 * t) * 0.1 * Math.exp(-t * 4);
          data[i] = envelope * (buzz + zap + sizzle) + static_;
        }
        break;

      case 'lightningBolt_impact':
        // Thunder crack with electrical discharge
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.exp(-t * 8) * Math.min(1, t * 200);
          // Sharp crack
          const crack = Math.exp(-t * 40) * (Math.random() - 0.5) * 0.7;
          // Thunder rumble
          const thunder = Math.sin(2 * Math.PI * 40 * t) * 0.5 * Math.exp(-t * 3);
          // Electrical remnant
          const remnant = Math.sin(2 * Math.PI * 800 * t * Math.exp(-t * 5)) * 0.2;
          data[i] = crack + envelope * (thunder + remnant);
        }
        break;

      case 'heal_cast':
      case 'healSpell_cast':
        // Warm, rising magical chime with harmonic shimmer
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 4) * Math.exp(-t * 1.2);
          // Ascending chime tones (major arpeggio)
          const notes = [261.63, 329.63, 392.00, 523.25]; // C E G C
          let value = 0;
          notes.forEach((freq, idx) => {
            const noteStart = idx * 0.15;
            if (t > noteStart) {
              const noteT = t - noteStart;
              const noteEnv = Math.exp(-noteT * 3) * Math.min(1, noteT * 30);
              value += noteEnv * Math.sin(2 * Math.PI * freq * t) * 0.15;
              value += noteEnv * Math.sin(2 * Math.PI * freq * 2 * t) * 0.06;
            }
          });
          // Warm shimmer
          const shimmer = Math.sin(2 * Math.PI * 8 * t) * 0.2 + 0.8;
          data[i] = envelope * value * shimmer;
        }
        break;

      case 'heartbeat':
        // Double-beat heartbeat: lub-DUB
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          // First beat (lub) at t=0
          const lub = Math.exp(-t * 20) * Math.sin(2 * Math.PI * 45 * t) * 0.6;
          // Second beat (dub) at t=0.15, slightly louder
          const dubT = t - 0.15;
          const dub = dubT > 0 ? Math.exp(-dubT * 25) * Math.sin(2 * Math.PI * 55 * dubT) * 0.8 : 0;
          // Low sub-bass body
          const body = Math.exp(-t * 8) * Math.sin(2 * Math.PI * 30 * t) * 0.3;
          data[i] = lub + dub + body;
        }
        break;

      case 'gesture_confirm':
        // Quick subtle confirmation click with magical sparkle
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const env = Math.exp(-t * 25);
          // Sharp click
          const click = Math.exp(-t * 60) * 0.3;
          // Tiny sparkle
          const sparkle = Math.sin(2 * Math.PI * 1200 * t) * 0.2 +
            Math.sin(2 * Math.PI * 1800 * t) * 0.1;
          data[i] = env * sparkle + click;
        }
        break;

      case 'combo_hit':
        // Rising tone burst for combo (base version)
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const env = Math.exp(-t * 12) * Math.min(1, t * 80);
          data[i] = env * (
            Math.sin(2 * Math.PI * 800 * t) * 0.3 +
            Math.sin(2 * Math.PI * 1200 * t) * 0.2 +
            Math.sin(2 * Math.PI * 1600 * t) * 0.1
          );
        }
        break;

      case 'achievement_unlock':
        // Triumphant ascending chime with magical sparkle
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 6) * Math.exp(-t * 0.8);
          // Ascending major arpeggio: C5 -> E5 -> G5 -> C6
          const notes = [
            { freq: 523.25, start: 0, dur: 0.4 },
            { freq: 659.25, start: 0.15, dur: 0.4 },
            { freq: 783.99, start: 0.3, dur: 0.4 },
            { freq: 1046.50, start: 0.45, dur: 0.6 },
          ];
          let value = 0;
          for (const note of notes) {
            if (t >= note.start && t < note.start + note.dur) {
              const noteT = t - note.start;
              const noteEnv = Math.min(1, noteT * 30) * Math.exp(-noteT * 2);
              value += noteEnv * Math.sin(2 * Math.PI * note.freq * t) * 0.2;
              value += noteEnv * Math.sin(2 * Math.PI * note.freq * 2 * t) * 0.08;
            }
          }
          // Sparkle shimmer at the end
          if (t > 0.5) {
            const shimmerT = t - 0.5;
            const shimmerEnv = Math.exp(-shimmerT * 2);
            value += shimmerEnv * Math.sin(2 * Math.PI * 2000 * t) * 0.03 * Math.sin(t * 40);
            value += shimmerEnv * Math.sin(2 * Math.PI * 3000 * t) * 0.02 * Math.sin(t * 30);
          }
          data[i] = envelope * value;
        }
        break;

      case 'cheer_applause':
        // Crowd clapping - layered noise bursts
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 5) * Math.exp(-t * 1.5);
          // Multiple clap bursts at different phases
          let claps = 0;
          for (let c = 0; c < 8; c++) {
            const clapPhase = (t * 6 + c * 0.125) % 0.25;
            const clapEnv = Math.exp(-clapPhase * 40) * (clapPhase < 0.05 ? 1 : 0.3);
            claps += clapEnv * (Math.random() - 0.5) * 0.3;
          }
          // Add some crowd murmur
          const murmur = (Math.random() - 0.5) * 0.1 * envelope;
          data[i] = envelope * claps + murmur;
        }
        break;

      case 'cheer_wow':
        // Crowd "oooh" rising pitch
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 8) * Math.exp(-t * 2);
          // Rising vowel sound with formants
          const baseFreq = 200 + t * 300;
          const formant1 = Math.sin(2 * Math.PI * baseFreq * t) * 0.3;
          const formant2 = Math.sin(2 * Math.PI * baseFreq * 2.5 * t) * 0.2;
          const formant3 = Math.sin(2 * Math.PI * baseFreq * 3.5 * t) * 0.1;
          // Add breathiness
          const breath = (Math.random() - 0.5) * 0.15 * envelope;
          data[i] = envelope * (formant1 + formant2 + formant3) + breath;
        }
        break;

      case 'cheer_laugh':
        // Crowd laughter - rhythmic bursts
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 6) * Math.exp(-t * 1.8);
          // "Ha ha ha" pattern
          const laughPhase = (t * 8) % 1;
          const laughBurst = Math.exp(-laughPhase * 15) * Math.sin(laughPhase * Math.PI);
          // Vocal-like frequencies
          const vocal = Math.sin(2 * Math.PI * 250 * t) * 0.3 +
                       Math.sin(2 * Math.PI * 500 * t) * 0.2 +
                       Math.sin(2 * Math.PI * 750 * t) * 0.1;
          // Add noise for breath
          const breath = (Math.random() - 0.5) * 0.2;
          data[i] = envelope * laughBurst * (vocal + breath);
        }
        break;

      case 'cheer_boo':
        // Crowd booing - low descending tone
        for (let i = 0; i < numSamples; i++) {
          const t = i / sampleRate;
          const envelope = Math.min(1, t * 4) * Math.exp(-t * 1.5);
          // Low descending "boo" sound
          const baseFreq = 180 - t * 50;
          const boo = Math.sin(2 * Math.PI * baseFreq * t) * 0.4 +
                     Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.2 +
                     Math.sin(2 * Math.PI * baseFreq * 3 * t) * 0.1;
          // Crowd rumble
          const rumble = (Math.random() - 0.5) * 0.2 * envelope;
          data[i] = envelope * boo + rumble;
        }
        break;
    }
  }

  private createAmbientMusic(ctx: AudioContext): void {
    const duration = 30;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    const baseFreqs = [65.41, 82.41, 98.00, 110.00];

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;

      let value = 0;
      const chordIndex = Math.floor(t / 8) % baseFreqs.length;
      const baseFreq = baseFreqs[chordIndex];

      value += Math.sin(2 * Math.PI * baseFreq * t) * 0.15;
      value += Math.sin(2 * Math.PI * baseFreq * 1.5 * t) * 0.1;
      value += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.08;

      value += Math.sin(2 * Math.PI * 0.1 * t) * Math.sin(2 * Math.PI * baseFreq * 3 * t) * 0.05;

      const lfo = Math.sin(2 * Math.PI * 0.05 * t);
      value *= 0.8 + lfo * 0.2;

      left[i] = value;
      right[i] = value * (0.9 + Math.sin(2 * Math.PI * 0.03 * t) * 0.1);
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url); // Track for cleanup

    this.music = new Howl({
      src: [url],
      volume: this.musicVolume,
      loop: true,
      format: ['wav']
    });
  }

  private createBattleMusic(ctx: AudioContext): void {
    const duration = 24; // 24 second loop for variety
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    // Musical parameters
    const bpm = 140;
    const beatDuration = 60 / bpm;

    // Dark minor key progression: Am - F - C - G (i - VI - III - VII)
    // Using A minor: A2=110Hz
    const chordProgression = [
      { root: 110.00, third: 130.81, fifth: 164.81 },  // Am (A, C, E)
      { root: 87.31, third: 110.00, fifth: 130.81 },   // F (F, A, C)
      { root: 130.81, third: 164.81, fifth: 196.00 },  // C (C, E, G)
      { root: 98.00, third: 123.47, fifth: 146.83 },   // G (G, B, D)
    ];

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const beat = t / beatDuration;
      const measure = Math.floor(beat / 4);
      const beatInMeasure = beat % 4;

      // Chord changes every 2 measures (8 beats)
      const chordIndex = Math.floor(measure / 2) % chordProgression.length;
      const chord = chordProgression[chordIndex];

      let value = 0;

      // === LAYER 1: Deep war drum pulse ===
      const drumPattern = beatInMeasure % 1;
      const isKickBeat = beatInMeasure < 0.1 || (beatInMeasure >= 2 && beatInMeasure < 2.1);
      const isSnarePosition = (beatInMeasure >= 1 && beatInMeasure < 1.15) ||
                              (beatInMeasure >= 3 && beatInMeasure < 3.15);

      // Kick drum - deep thump
      if (isKickBeat) {
        const kickT = drumPattern < 0.1 ? drumPattern : (drumPattern - 2);
        const kickEnv = Math.exp(-Math.abs(kickT) * 25);
        value += kickEnv * Math.sin(2 * Math.PI * 55 * Math.exp(-Math.abs(kickT) * 8) * t) * 0.35;
      }

      // Snare/tom hit
      if (isSnarePosition) {
        const snareT = beatInMeasure % 1;
        const snareEnv = Math.exp(-snareT * 20);
        value += snareEnv * (
          Math.sin(2 * Math.PI * 180 * t) * 0.15 +
          (Math.random() - 0.5) * 0.12
        );
      }

      // === LAYER 2: Driving bass line ===
      const bassFreq = chord.root / 2; // One octave down
      const bassEnv = 0.7 + Math.sin(2 * Math.PI * beat * 2) * 0.3; // Pulse with beat
      value += Math.sin(2 * Math.PI * bassFreq * t) * 0.18 * bassEnv;
      value += Math.sin(2 * Math.PI * bassFreq * 2 * t) * 0.08 * bassEnv; // Overtone

      // === LAYER 3: Dark pad chords ===
      const padSwell = 0.6 + Math.sin(2 * Math.PI * t / 4) * 0.4; // Slow swell
      const padVibrato = 1 + Math.sin(2 * Math.PI * 5 * t) * 0.003; // Subtle vibrato

      // Root
      value += Math.sin(2 * Math.PI * chord.root * padVibrato * t) * 0.08 * padSwell;
      // Minor third
      value += Math.sin(2 * Math.PI * chord.third * padVibrato * t) * 0.06 * padSwell;
      // Fifth
      value += Math.sin(2 * Math.PI * chord.fifth * padVibrato * t) * 0.05 * padSwell;
      // Octave up
      value += Math.sin(2 * Math.PI * chord.root * 2 * padVibrato * t) * 0.04 * padSwell;

      // === LAYER 4: Mystical arpeggios ===
      const arpSpeed = 4; // Notes per beat
      const arpNote = Math.floor(beat * arpSpeed) % 4;
      const arpFreqs = [chord.root * 2, chord.third * 2, chord.fifth * 2, chord.root * 4];
      const arpT = (beat * arpSpeed) % 1;
      const arpEnv = Math.exp(-arpT * 6) * (1 - Math.exp(-arpT * 30));

      // Shimmering arpeggio
      value += Math.sin(2 * Math.PI * arpFreqs[arpNote] * t) * 0.06 * arpEnv;
      value += Math.sin(2 * Math.PI * arpFreqs[arpNote] * 2 * t) * 0.03 * arpEnv;

      // === LAYER 5: Tension risers (every 8 measures) ===
      const sectionProgress = (measure % 8) / 8;
      if (sectionProgress > 0.75) {
        const riserAmount = (sectionProgress - 0.75) * 4;
        const riserFreq = 200 + riserAmount * 600;
        value += Math.sin(2 * Math.PI * riserFreq * t) * 0.04 * riserAmount;
        // Add noise sweep
        value += (Math.random() - 0.5) * 0.03 * riserAmount;
      }

      // === LAYER 6: Magical shimmer ===
      const shimmerFreq = 2000 + Math.sin(2 * Math.PI * 0.2 * t) * 500;
      const shimmerEnv = 0.02 + Math.sin(2 * Math.PI * 0.5 * t) * 0.01;
      value += Math.sin(2 * Math.PI * shimmerFreq * t) * shimmerEnv * 0.5;

      // === LAYER 7: Sub bass rumble ===
      value += Math.sin(2 * Math.PI * 30 * t) * 0.05;

      // Stereo spread
      const stereoPhase = Math.sin(2 * Math.PI * 0.1 * t) * 0.1;
      left[i] = value * (1 + stereoPhase);
      right[i] = value * (1 - stereoPhase);

      // Add subtle stereo delay effect on right channel
      if (i > sampleRate * 0.015) {
        right[i] = right[i] * 0.7 + left[i - Math.floor(sampleRate * 0.015)] * 0.3;
      }
    }

    // Normalize to prevent clipping
    let maxVal = 0;
    for (let i = 0; i < numSamples; i++) {
      maxVal = Math.max(maxVal, Math.abs(left[i]), Math.abs(right[i]));
    }
    if (maxVal > 0.9) {
      const normalize = 0.85 / maxVal;
      for (let i = 0; i < numSamples; i++) {
        left[i] *= normalize;
        right[i] *= normalize;
      }
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url); // Track for cleanup

    this.battleMusic = new Howl({
      src: [url],
      volume: this.musicVolume * 0.8,
      loop: true,
      format: ['wav']
    });
  }

  // Intense battle music layer: heavier drums, faster arpeggios, more tension
  private createBattleMusicIntense(ctx: AudioContext): void {
    const duration = 24;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    const bpm = 140;
    const beatDuration = 60 / bpm;

    const chordProgression = [
      { root: 110.00, third: 130.81, fifth: 164.81 },
      { root: 87.31, third: 110.00, fifth: 130.81 },
      { root: 130.81, third: 164.81, fifth: 196.00 },
      { root: 98.00, third: 123.47, fifth: 146.83 },
    ];

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const beat = t / beatDuration;
      const measure = Math.floor(beat / 4);
      const beatInMeasure = beat % 4;
      const chordIndex = Math.floor(measure / 2) % chordProgression.length;
      const chord = chordProgression[chordIndex];

      let value = 0;

      // === Aggressive double-time kick ===
      const eighthNote = beatInMeasure % 0.5;
      if (eighthNote < 0.05) {
        const kickEnv = Math.exp(-eighthNote * 50);
        value += kickEnv * Math.sin(2 * Math.PI * 50 * Math.exp(-eighthNote * 12) * t) * 0.4;
      }

      // === Heavy snare on every beat ===
      const beatFrac = beatInMeasure % 1;
      if (beatFrac >= 0.5 && beatFrac < 0.6) {
        const snareT = beatFrac - 0.5;
        const snareEnv = Math.exp(-snareT * 30);
        value += snareEnv * (
          Math.sin(2 * Math.PI * 200 * t) * 0.2 +
          (Math.random() - 0.5) * 0.25
        );
      }

      // === Hi-hat sixteenth notes ===
      const sixteenth = beatInMeasure % 0.25;
      if (sixteenth < 0.02) {
        value += Math.exp(-sixteenth * 100) * (Math.random() - 0.5) * 0.15;
      }

      // === Aggressive bass (octave lower, distorted) ===
      const bassFreq = chord.root / 2;
      const bassVal = Math.sin(2 * Math.PI * bassFreq * t);
      // Soft clipping for distortion
      value += Math.tanh(bassVal * 2) * 0.15;

      // === Tension chord stabs ===
      const stabPattern = beat % 2;
      if (stabPattern < 0.15) {
        const stabEnv = Math.exp(-stabPattern * 15);
        value += stabEnv * (
          Math.sin(2 * Math.PI * chord.root * 2 * t) * 0.1 +
          Math.sin(2 * Math.PI * chord.third * 2 * t) * 0.08 +
          Math.sin(2 * Math.PI * chord.fifth * 2 * t) * 0.06
        );
      }

      // === Fast arpeggios (double speed) ===
      const arpSpeed = 8;
      const arpNote = Math.floor(beat * arpSpeed) % 4;
      const arpFreqs = [chord.root * 4, chord.third * 4, chord.fifth * 4, chord.root * 8];
      const arpT = (beat * arpSpeed) % 1;
      const arpEnv = Math.exp(-arpT * 10) * (1 - Math.exp(-arpT * 50));
      value += Math.sin(2 * Math.PI * arpFreqs[arpNote] * t) * 0.04 * arpEnv;

      // === Continuous noise sweep for urgency ===
      const sweepFreq = 800 + Math.sin(2 * Math.PI * 0.5 * t) * 400;
      value += Math.sin(2 * Math.PI * sweepFreq * t) * 0.02;
      value += (Math.random() - 0.5) * 0.03;

      // === Pulsing sub bass ===
      const subPulse = Math.sin(2 * Math.PI * beat * 2) * 0.5 + 0.5;
      value += Math.sin(2 * Math.PI * 25 * t) * 0.06 * subPulse;

      // Stereo
      const stereoPhase = Math.sin(2 * Math.PI * 0.15 * t) * 0.15;
      left[i] = value * (1 + stereoPhase);
      right[i] = value * (1 - stereoPhase);

      if (i > sampleRate * 0.01) {
        right[i] = right[i] * 0.6 + left[i - Math.floor(sampleRate * 0.01)] * 0.4;
      }
    }

    // Normalize
    let maxVal = 0;
    for (let i = 0; i < numSamples; i++) {
      maxVal = Math.max(maxVal, Math.abs(left[i]), Math.abs(right[i]));
    }
    if (maxVal > 0.9) {
      const normalize = 0.85 / maxVal;
      for (let i = 0; i < numSamples; i++) {
        left[i] *= normalize;
        right[i] *= normalize;
      }
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);

    this.battleMusicIntense = new Howl({
      src: [url],
      volume: 0, // Starts silent, fades in with intensity
      loop: true,
      format: ['wav']
    });
  }

  // Arena ambience: torch crackling, wind, distant crowd murmur
  private createArenaAmbience(ctx: AudioContext): void {
    const duration = 20;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;

      let valueL = 0;
      let valueR = 0;

      // === Torch crackling (left-biased, like torches on arena walls) ===
      const crackleRate = 20 + Math.sin(t * 3) * 10;
      const cracklePhase = (t * crackleRate) % 1;
      if (cracklePhase < 0.05) {
        const crackleEnv = Math.exp(-cracklePhase * 80);
        const crackle = crackleEnv * (Math.random() - 0.3) * 0.15;
        valueL += crackle * 1.2;
        valueR += crackle * 0.5;
      }
      // Constant low fire hiss
      const fireHiss = (Math.random() - 0.5) * 0.02;
      valueL += fireHiss;
      valueR += fireHiss * 0.7;

      // === Wind gusts (sweeping stereo) ===
      const windSwell = Math.sin(2 * Math.PI * 0.08 * t) * 0.5 + 0.5;
      const windBase = (Math.random() - 0.5) * 0.03 * windSwell;
      const windPan = Math.sin(2 * Math.PI * 0.12 * t); // Pans left-right
      valueL += windBase * (1 + windPan);
      valueR += windBase * (1 - windPan);

      // Gentle wind whistle
      const whistleFreq = 600 + Math.sin(2 * Math.PI * 0.1 * t) * 200;
      const whistle = Math.sin(2 * Math.PI * whistleFreq * t) * 0.005 * windSwell;
      valueL += whistle;
      valueR += whistle;

      // === Distant crowd murmur (background hum) ===
      const murmurEnv = 0.6 + Math.sin(2 * Math.PI * 0.15 * t) * 0.4;
      const murmur = (Math.random() - 0.5) * 0.015 * murmurEnv;
      // Low-frequency crowd rumble
      const crowdRumble = Math.sin(2 * Math.PI * 80 * t) * 0.005 * murmurEnv +
        Math.sin(2 * Math.PI * 120 * t) * 0.003 * murmurEnv;
      valueL += murmur + crowdRumble;
      valueR += murmur * 0.9 + crowdRumble;

      left[i] = valueL;
      right[i] = valueR;
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url);

    this.arenaAmbience = new Howl({
      src: [url],
      volume: this.musicVolume * 0.4,
      loop: true,
      format: ['wav']
    });
  }

  private createVictoryMusic(ctx: AudioContext): void {
    // 4 bars at 120 BPM = 8 seconds
    const duration = 8;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    const bpm = 120;
    const beatDuration = 60 / bpm;

    // Triumphant C major fanfare
    // Chord progression: C - G - Am - F - C - G - C - C
    const chords = [
      { notes: [261.63, 329.63, 392.00] },  // C major
      { notes: [392.00, 493.88, 587.33] },  // G major
      { notes: [440.00, 523.25, 659.25] },  // A minor
      { notes: [349.23, 440.00, 523.25] },  // F major
      { notes: [261.63, 329.63, 392.00] },  // C major
      { notes: [392.00, 493.88, 587.33] },  // G major
      { notes: [523.25, 659.25, 783.99] },  // C major (octave up)
      { notes: [523.25, 659.25, 783.99] },  // C major (hold)
    ];

    // Fanfare melody: C5 - E5 - G5 - C6 (rising triumph)
    const melodyNotes = [
      { freq: 523.25, start: 0, dur: 0.5 },      // C5
      { freq: 659.25, start: 0.5, dur: 0.5 },    // E5
      { freq: 783.99, start: 1.0, dur: 0.5 },    // G5
      { freq: 1046.50, start: 1.5, dur: 1.0 },   // C6 (held)
      { freq: 783.99, start: 3.0, dur: 0.5 },    // G5
      { freq: 1046.50, start: 3.5, dur: 0.5 },   // C6
      { freq: 1174.66, start: 4.0, dur: 0.5 },   // D6
      { freq: 1318.51, start: 4.5, dur: 1.5 },   // E6 (triumphant hold)
      { freq: 1046.50, start: 6.0, dur: 2.0 },   // C6 (final)
    ];

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const beat = t / beatDuration;
      const chordIndex = Math.min(Math.floor(beat / 2), chords.length - 1);
      const chord = chords[chordIndex];

      let value = 0;

      // Fade envelope for the whole piece
      const masterEnv = Math.min(1, t * 4) * Math.min(1, (duration - t) * 2);

      // === Brass-like chord stabs ===
      const stabPhase = beat % 2;
      const stabEnv = stabPhase < 0.5 ? Math.exp(-stabPhase * 3) : 0.3;

      for (const freq of chord.notes) {
        // Brass-like harmonics
        value += Math.sin(2 * Math.PI * freq * t) * 0.08 * stabEnv;
        value += Math.sin(2 * Math.PI * freq * 2 * t) * 0.05 * stabEnv;
        value += Math.sin(2 * Math.PI * freq * 3 * t) * 0.03 * stabEnv;
      }

      // === Fanfare melody ===
      for (const note of melodyNotes) {
        if (t >= note.start && t < note.start + note.dur) {
          const noteT = t - note.start;
          const noteEnv = Math.min(1, noteT * 20) * Math.exp(-noteT * 0.5);
          // Bright trumpet-like tone
          value += Math.sin(2 * Math.PI * note.freq * t) * 0.15 * noteEnv;
          value += Math.sin(2 * Math.PI * note.freq * 2 * t) * 0.08 * noteEnv;
          value += Math.sin(2 * Math.PI * note.freq * 3 * t) * 0.04 * noteEnv;
          value += Math.sin(2 * Math.PI * note.freq * 4 * t) * 0.02 * noteEnv;
        }
      }

      // === Timpani hits on strong beats ===
      const timpaniBeats = [0, 2, 4, 6, 7, 7.5];
      for (const timpBeat of timpaniBeats) {
        const timpT = t - timpBeat * beatDuration;
        if (timpT > 0 && timpT < 0.5) {
          const timpEnv = Math.exp(-timpT * 8);
          value += Math.sin(2 * Math.PI * 65 * Math.exp(-timpT * 3) * t) * 0.2 * timpEnv;
        }
      }

      // === Cymbal shimmer ===
      if (t > 6) {
        const cymbalT = t - 6;
        const cymbalEnv = Math.exp(-cymbalT * 1.5);
        value += (Math.random() - 0.5) * 0.1 * cymbalEnv;
      }

      // === Rising sparkle at the end ===
      if (t > 5) {
        const sparkleFreq = 2000 + (t - 5) * 500;
        value += Math.sin(2 * Math.PI * sparkleFreq * t) * 0.02 * Math.sin(t * 30);
      }

      // Apply master envelope
      value *= masterEnv;

      // Stereo
      left[i] = value;
      right[i] = value * 0.95 + (Math.random() - 0.5) * 0.01;
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url); // Track for cleanup

    this.victoryMusic = new Howl({
      src: [url],
      volume: this.musicVolume * 1.2,
      loop: false,
      format: ['wav']
    });
  }

  private createDefeatMusic(ctx: AudioContext): void {
    // 4 bars at 80 BPM (slower, more somber) = 12 seconds
    const duration = 12;
    const sampleRate = ctx.sampleRate;
    const numSamples = duration * sampleRate;
    const buffer = ctx.createBuffer(2, numSamples, sampleRate);
    const left = buffer.getChannelData(0);
    const right = buffer.getChannelData(1);

    const bpm = 80;
    const beatDuration = 60 / bpm;

    // Somber A minor descending progression
    // Am - Em - Dm - Am (descending feeling)
    const chords = [
      { notes: [220.00, 261.63, 329.63] },  // A minor
      { notes: [164.81, 196.00, 246.94] },  // E minor
      { notes: [146.83, 174.61, 220.00] },  // D minor
      { notes: [110.00, 130.81, 164.81] },  // A minor (low)
    ];

    // Descending melody (lament)
    const melodyNotes = [
      { freq: 440.00, start: 0, dur: 1.5 },      // A4
      { freq: 392.00, start: 1.5, dur: 1.5 },    // G4
      { freq: 349.23, start: 3.0, dur: 1.5 },    // F4
      { freq: 329.63, start: 4.5, dur: 1.5 },    // E4
      { freq: 293.66, start: 6.0, dur: 1.5 },    // D4
      { freq: 261.63, start: 7.5, dur: 1.5 },    // C4
      { freq: 246.94, start: 9.0, dur: 1.5 },    // B3
      { freq: 220.00, start: 10.5, dur: 1.5 },   // A3 (final, low)
    ];

    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      const beat = t / beatDuration;
      const chordIndex = Math.min(Math.floor(beat / 4), chords.length - 1);
      const chord = chords[chordIndex];

      let value = 0;

      // Fade envelope - gradual fade out
      const masterEnv = Math.min(1, t * 2) * Math.pow(Math.max(0, 1 - t / duration), 0.5);

      // === Somber pad chords ===
      const padSwell = 0.7 + Math.sin(2 * Math.PI * t / 3) * 0.3;

      for (const freq of chord.notes) {
        // Soft, mournful pad
        const vibrato = 1 + Math.sin(2 * Math.PI * 4 * t) * 0.005;
        value += Math.sin(2 * Math.PI * freq * vibrato * t) * 0.06 * padSwell;
        value += Math.sin(2 * Math.PI * freq * 2 * vibrato * t) * 0.03 * padSwell;
      }

      // === Descending melody ===
      for (const note of melodyNotes) {
        if (t >= note.start && t < note.start + note.dur) {
          const noteT = t - note.start;
          // Slow attack, gradual decay
          const noteEnv = (1 - Math.exp(-noteT * 3)) * Math.exp(-noteT * 0.3);
          // Mournful, slightly detuned for sadness
          const detune = Math.sin(2 * Math.PI * 5 * t) * 0.003;
          value += Math.sin(2 * Math.PI * note.freq * (1 + detune) * t) * 0.12 * noteEnv;
          value += Math.sin(2 * Math.PI * note.freq * 2 * t) * 0.04 * noteEnv;
        }
      }

      // === Deep bell tolls ===
      const bellBeats = [0, 4, 8, 12];
      for (const bellBeat of bellBeats) {
        const bellT = t - bellBeat * beatDuration;
        if (bellT > 0 && bellT < 3) {
          const bellEnv = Math.exp(-bellT * 0.8);
          // Funeral bell
          value += Math.sin(2 * Math.PI * 110 * t) * 0.08 * bellEnv;
          value += Math.sin(2 * Math.PI * 220 * t) * 0.04 * bellEnv;
          value += Math.sin(2 * Math.PI * 277 * t) * 0.02 * bellEnv; // Slightly dissonant
        }
      }

      // === Subtle wind/sigh effect ===
      const windEnv = 0.02 + Math.sin(2 * Math.PI * 0.2 * t) * 0.01;
      value += (Math.random() - 0.5) * windEnv * masterEnv;

      // === Sub bass drone ===
      value += Math.sin(2 * Math.PI * 55 * t) * 0.04 * masterEnv;

      // Apply master envelope
      value *= masterEnv;

      // Stereo with slight chorus effect for eeriness
      const chorusOffset = Math.sin(2 * Math.PI * 0.5 * t) * 0.002;
      left[i] = value;
      right[i] = value * (1 + chorusOffset);
    }

    const wav = this.bufferToWav(buffer);
    const blob = new Blob([wav], { type: 'audio/wav' });
    const url = URL.createObjectURL(blob);
    this.blobUrls.push(url); // Track for cleanup

    this.defeatMusic = new Howl({
      src: [url],
      volume: this.musicVolume,
      loop: false,
      format: ['wav']
    });
  }

  private bufferToWav(buffer: AudioBuffer): ArrayBuffer {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const format = 1;
    const bitDepth = 16;

    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;

    const samples = buffer.length;
    const dataSize = samples * blockAlign;
    const bufferSize = 44 + dataSize;

    const arrayBuffer = new ArrayBuffer(bufferSize);
    const view = new DataView(arrayBuffer);

    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    writeString(0, 'RIFF');
    view.setUint32(4, bufferSize - 8, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, format, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataSize, true);

    const channels: Float32Array[] = [];
    for (let i = 0; i < numChannels; i++) {
      channels.push(buffer.getChannelData(i));
    }

    let offset = 44;
    for (let i = 0; i < samples; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i]));
        const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        view.setInt16(offset, intSample, true);
        offset += 2;
      }
    }

    return arrayBuffer;
  }

  play(name: SoundName): void {
    if (this.isMuted || !this.isInitialized) return;

    const sound = this.sounds.get(name);
    if (sound) {
      sound.volume(this.sfxVolume);
      sound.play();
    }
  }

  playCheer(cheerType: CheerType): void {
    const soundName = `cheer_${cheerType}` as SoundName;
    this.play(soundName);
  }

  playMusic(): void {
    if (this.isMuted || !this.music) return;
    this.stopBattleMusic();
    this.music.volume(this.musicVolume);
    this.music.play();
  }

  stopMusic(): void {
    if (this.music) {
      this.music.stop();
    }
  }

  playBattleMusic(): void {
    if (this.isMuted || !this.battleMusic) return;
    this.stopMusic();

    // Reset intensity
    this.currentIntensity = 0;
    this.targetIntensity = 0;

    // Play both layers, intense starts silent
    this.battleMusic.volume(this.musicVolume * 0.8);
    this.battleMusic.play();

    if (this.battleMusicIntense) {
      this.battleMusicIntense.volume(0);
      this.battleMusicIntense.play();
    }

    // Start arena ambience
    if (this.arenaAmbience) {
      this.arenaAmbience.volume(this.musicVolume * 0.4);
      this.arenaAmbience.play();
    }
  }

  stopBattleMusic(): void {
    if (this.battleMusic) {
      this.battleMusic.stop();
    }
    if (this.battleMusicIntense) {
      this.battleMusicIntense.stop();
    }
    if (this.arenaAmbience) {
      this.arenaAmbience.stop();
    }
    this.stopHeartbeat();

    // Stop intensity ramping
    if (this.intensityRampTimer) {
      clearInterval(this.intensityRampTimer);
      this.intensityRampTimer = null;
    }
  }

  playVictoryMusic(): void {
    if (this.isMuted || !this.victoryMusic) return;
    this.stopBattleMusic();
    this.stopMusic();
    this.victoryMusic.volume(this.musicVolume * 1.2);
    this.victoryMusic.play();
  }

  playDefeatMusic(): void {
    if (this.isMuted || !this.defeatMusic) return;
    this.stopBattleMusic();
    this.stopMusic();
    this.defeatMusic.volume(this.musicVolume);
    this.defeatMusic.play();
  }

  setMusicVolume(volume: number): void {
    this.musicVolume = Math.max(0, Math.min(1, volume));
    if (this.music) {
      this.music.volume(this.musicVolume);
    }
    if (this.arenaAmbience) {
      this.arenaAmbience.volume(this.musicVolume * 0.4);
    }
  }

  setSFXVolume(volume: number): void {
    this.sfxVolume = Math.max(0, Math.min(1, volume));
  }

  mute(): void {
    this.isMuted = true;
    Howler.mute(true);
  }

  unmute(): void {
    this.isMuted = false;
    Howler.mute(false);
  }

  toggleMute(): boolean {
    if (this.isMuted) {
      this.unmute();
    } else {
      this.mute();
    }
    return this.isMuted;
  }

  dispose(): void {
    this.sounds.forEach(sound => sound.unload());
    this.sounds.clear();
    this.wizardSounds.forEach(sound => sound.unload());
    this.wizardSounds.clear();
    if (this.music) {
      this.music.unload();
      this.music = null;
    }
    if (this.battleMusic) {
      this.battleMusic.unload();
      this.battleMusic = null;
    }
    if (this.battleMusicIntense) {
      this.battleMusicIntense.unload();
      this.battleMusicIntense = null;
    }
    if (this.victoryMusic) {
      this.victoryMusic.unload();
      this.victoryMusic = null;
    }
    if (this.defeatMusic) {
      this.defeatMusic.unload();
      this.defeatMusic = null;
    }
    if (this.arenaAmbience) {
      this.arenaAmbience.unload();
      this.arenaAmbience = null;
    }
    // Stop timers
    this.stopHeartbeat();
    if (this.intensityRampTimer) {
      clearInterval(this.intensityRampTimer);
      this.intensityRampTimer = null;
    }
    // Revoke all blob URLs to free memory
    this.blobUrls.forEach(url => URL.revokeObjectURL(url));
    this.blobUrls = [];
    // Close AudioContext
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }
}
